import React, { forwardRef } from 'react';
import {
    Button as ChakraButton,
    type ButtonProps as ChakraButtonProps,
    useStyleConfig,
} from '@chakra-ui/react';
import { Text } from '../typography';
import { mapButtonVariantToTextStyle } from './const';
import type { CommonStyles } from '../types';
import type { TextSize, TextStyle, TextTypes } from '../typography/text/types';
import type { ButtonTypes } from './types';

export interface ButtonProps extends CommonStyles, Omit<ChakraButtonProps, keyof CommonStyles> {
    children?: React.ReactNode;
    // isDisabled - apply disabled styles to button, stop any interactions
    isDisabled?: boolean;
    // isLoading -spot button interactions without applying disabled styles
    isLoading?: boolean;
    variant?: ButtonTypes;
    leftIcon?: React.ReactElement;
    rightIcon?: React.ReactElement;
    wide?: boolean;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    id?: string;
    borderRightRadius?: number;
    borderLeftRadius?: number;
    isActive?: boolean;
    color?: string;
    textSize?: TextSize;
    textMargin?: string;
    textStyle?: TextStyle;
    trim?: boolean;
    value?: string | number | readonly string[];
    minWidth?: string;
    borderWidth?: number;
    className?: string;
    kind?: 'button' | 'submit' | 'reset';
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            children,
            isDisabled = false,
            variant = 'primary',
            leftIcon,
            rightIcon,
            isLoading = false,
            wide = false,
            id,
            onClick,
            borderRightRadius,
            borderLeftRadius,
            isActive = false,
            color,
            margin,
            padding,
            display,
            textSize,
            textMargin,
            textStyle,
            trim = false,
            value,
            minWidth = 'unset',
            className,
            kind = 'button',
            borderWidth,
            textTransform,
            ...props
        },
        ref,
    ) => {
        // TODO should be deprecated once existed checkout button theme aligned with new design system
        const getVariant = `new${variant}`;
        const textProps = mapButtonVariantToTextStyle[variant];
        const themeColor = useStyleConfig('Button', { variant: getVariant }).color;
        return (
            <ChakraButton
                isActive={isActive}
                isDisabled={isDisabled}
                variant={getVariant}
                leftIcon={leftIcon}
                rightIcon={rightIcon}
                pointerEvents={isLoading ? 'none' : 'all'}
                onClick={onClick}
                data-testid={id}
                ref={ref}
                borderRight={borderRightRadius}
                borderRightRadius={borderRightRadius}
                borderLeft={borderLeftRadius}
                borderLeftRadius={borderLeftRadius}
                iconSpacing={0}
                isLoading={isLoading}
                display={display}
                minWidth={minWidth}
                sx={{
                    margin,
                    padding,
                    width: wide ? '100%' : 'unset',
                    color: color || themeColor,
                    '> .chakra-button__icon:not([hidden])': {
                        marginLeft: '0',
                    },
                }}
                className={className}
                value={value}
                type={kind}
                borderWidth={borderWidth}
                {...props}
            >
                {children && (
                    <Text
                        type={textProps.type as TextTypes}
                        style={textStyle || (textProps.style as TextStyle)}
                        size={textSize || (textProps.size as TextSize)}
                        as="span"
                        margin={textMargin}
                        textTransform={textTransform}
                        color="inherit"
                        trim={trim}
                    >
                        {children}
                    </Text>
                )}
            </ChakraButton>
        );
    },
);

Button.displayName = 'Button';

Button.defaultProps = {
    id: undefined,
    isDisabled: undefined,
    isLoading: undefined,
    variant: undefined,
    leftIcon: undefined,
    rightIcon: undefined,
    wide: undefined,
    onClick: undefined,
    borderRightRadius: undefined,
    borderLeftRadius: undefined,
    isActive: undefined,
    color: undefined,
    textSize: undefined,
    textMargin: undefined,
    textStyle: undefined,
    trim: undefined,
    value: undefined,
    minWidth: undefined,
    className: undefined,
    kind: 'button',
    borderWidth: undefined,
    children: undefined,
};
